'use client';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { usePathname } from 'next/navigation';
import { theme } from '~/theme';
import clsx from 'clsx';
import styles from './Link.module.scss';

type AnchorProps = React.HTMLProps<HTMLAnchorElement>;

export type LinkProps = Pick<AnchorProps, 'target' | 'title' | 'className' | 'tabIndex'> & {
    /**
     * The path or URL to navigate to. This is the only required prop.
     */
    href?: NextLinkProps['href'];

    /**
     * Refetch the page in the background. Any `<Link />` that is in the viewport (initially or through scroll) will be preloaded.
     * When prefetch is set to false, prefetching will still occur on hover.
     * Pages using Static Generation will preload JSON files with the data for faster page transitions.
     * _Prefetching is only enabled in production_.
     */
    prefetch?: NextLinkProps['prefetch'];

    /**
     *  Replace the current history state instead of adding a new url into the stack.
     */
    replace?: NextLinkProps['replace'];

    /**
     *  Scroll to the top of the page after a navigation
     */
    scroll?: NextLinkProps['scroll'];

    /**
     * Update the path of the current page without rerunning `getStaticProps`, `getServerSideProps` or `getInitialProps`
     */
    shallow?: NextLinkProps['shallow'];
    noHover?: boolean;
    color?: string;
    rel?: string;
    children: React.ReactNode;
    onClick?: (e: React.SyntheticEvent) => void;
    activeClassName?: string;
};

export const Link = ({
    href,
    prefetch,
    replace,
    shallow,
    color,
    scroll = true,
    title,
    rel,
    className = '',
    activeClassName = '',
    noHover,
    ...rest
}: LinkProps) => {
    const pathname = usePathname();
    const isLinkActive = pathname === href;
    const mergedClassName = clsx(className, isLinkActive && activeClassName);

    /**
     * Use a plain <a /> when compositioned with a button,
     * to prevent style conflicts
     */
    const isCompositionedButton = className.match(/css-(.+)-(.+)?Button/);

    if (isCompositionedButton) {
        return (
            <NextLink
                aria-label={title}
                href={href || ''}
                passHref
                prefetch={prefetch}
                replace={replace}
                scroll={scroll}
                rel={rel}
                shallow={shallow}
                css={{ color: color }}
                className={mergedClassName}
                {...rest}
            />
        );
    }

    const inlineStyle: { [key: `--${string}`]: string | number } = {
        '--link-text-color': color || theme.colors.primary,
    };

    return (
        <NextLink
            aria-label={title}
            href={href || ''}
            passHref
            rel={rel}
            prefetch={prefetch}
            replace={replace}
            scroll={scroll}
            shallow={shallow}
            style={inlineStyle}
            className={clsx(
                styles.component,
                { [styles.noHover]: noHover },
                className,
                isLinkActive && activeClassName,
            )}
            {...rest}
        />
    );
};
